import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Lock } from "lucide-react";
import { toast } from "sonner";
import { EXPENSE_CATS } from "@/components/finance/ExpensesCard";
import { CategoryPicker } from "@/components/common/CategoryPicker";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { cn } from "@/lib/utils";

export const CATS = { ...EXPENSE_CATS, banque: "Opérations bancaires" };

const catUrl = (o) => (o.id.startsWith("t:") ? `/finance/transactions/${o.id.split(":")[2]}/category` : o.id.startsWith("e:") ? `/finance/expenses/${o.id.slice(2)}/category` : null);

export const OpRow = ({ o, i }) => {
  const { money } = useFormat();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const refresh = () => ["planning", "tx-review", "health", "expense-summary", "budgets", "cat-frequent"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const url = catUrl(o);
  const setCat = async (category) => {
    try {
      const { data } = await api.patch(url, { category });
      toast.success(data.also_updated ? `Catégorie modifiée, ${data.also_updated} opération(s) similaire(s) mise(s) à jour` : "Catégorie modifiée");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const check = async () => {
    try {
      await api.put(`/planning/personal/me/operations/${encodeURIComponent(o.id)}/check`, { checked: !o.checked });
      toast.success(o.checked ? "Pointage retiré" : "Opération pointée");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const togglePrivate = async () => {
    try {
      await api.put(`/finance/transactions/${o.id.split(":")[2]}/private`, { private: !o.private });
      toast.success(o.private ? "Opération de nouveau visible par le foyer" : "Opération masquée aux membres qui voient ce compte");
      refresh(); qc.invalidateQueries({ queryKey: ["bank-txs"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <li className="py-1" data-testid={`month-top-${i}`}>
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 rounded-lg py-1.5 text-left text-sm transition-colors hover:bg-secondary/50" data-testid={`month-op-toggle-${i}`}>
        <span className="num w-4 shrink-0 text-xs text-muted-foreground">{i + 1}</span>
        <span className="min-w-0 flex-1"><span className={cn("flex items-center gap-1 truncate font-medium", o.hidden && "italic text-muted-foreground")}>{(o.hidden || o.private) && <Lock className="h-3.5 w-3.5 shrink-0" aria-label="privée" />}{o.label}{o.checked && <Check className="h-3.5 w-3.5 shrink-0 text-success" aria-label="pointée" />}</span>
          <span className="text-[11px] text-muted-foreground">{new Date(o.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} · {CATS[o.category] || o.category} · {o.source}</span></span>
        <span className="num shrink-0 font-semibold">{o.amount === null ? "•••" : money(o.amount, { maximumFractionDigits: 2 })}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open && o.hidden && <p className="ml-6 mt-1 rounded-xl bg-secondary/60 p-2.5 text-xs text-muted-foreground">Opération masquée par le titulaire du compte. Son montant reste compté dans les totaux.</p>}
      {open && !o.hidden && (
        <div className="ml-6 mt-1 flex flex-wrap items-center gap-2 rounded-xl bg-secondary/60 p-2.5" data-testid={`month-op-editor-${i}`}>
          {url ? (
            <CategoryPicker cats={CATS} frequentUrl="/finance/categories/frequent" value={o.category} onPick={setCat} op={{ date: o.date, label: o.label, amount: -Math.abs(o.amount || 0) }} triggerClass="w-44 bg-card" testid={`month-op-category-${i}`} />
          ) : <span className="text-xs text-muted-foreground">Catégorie modifiable depuis le budget du foyer</span>}
          <button type="button" onClick={check} className={cn("flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-semibold transition-transform active:scale-95", o.checked ? "bg-success text-white" : "border border-border bg-card")} data-testid={`month-op-check-${i}`}>
            <Check className="h-3.5 w-3.5" strokeWidth={3} />{o.checked ? "Pointée" : "Pointer"}
          </button>
          {o.id.startsWith("t:") && o.source === "Banque" && (
            <button type="button" onClick={togglePrivate} className={cn("flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-semibold", o.private ? "bg-warning text-white" : "border border-border bg-card")} data-testid={`month-op-private-${i}`}>
              <Lock className="h-3.5 w-3.5" />{o.private ? "Masquée au foyer" : "Masquer au foyer"}
            </button>
          )}
        </div>
      )}
    </li>
  );
};
